import { useEffect, useRef, useState, KeyboardEvent } from 'react'
import { Badge } from '@/components/ui/badge'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TagInputProps {
  value: string[]
  onChange: (next: string[]) => void
  availableTags: string[]
  placeholder?: string
  maxLength?: number
  disabled?: boolean
  className?: string
}

/**
 * Reusable tag input：
 * - 已選 tag 顯示為可移除的 chip
 * - 輸入時 filter availableTags 出建議清單；點擊或 Enter 加入
 * - 沒匹配時 Enter / 點擊「新增」加入新 tag
 * - 大小寫保留（"Vip" 與 "vip" 為不同 tag）
 * - Backspace 在 input 為空時刪除最後一個 chip
 */
export function TagInput({
  value,
  onChange,
  availableTags,
  placeholder = '輸入標籤後按 Enter，或從建議清單選擇',
  maxLength = 50,
  disabled,
  className,
}: TagInputProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // 點容器外面就關閉建議清單
  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const trimmed = query.trim()
  const selectedSet = new Set(value)
  const suggestions = availableTags.filter(
    (t) =>
      !selectedSet.has(t) &&
      (trimmed === '' || t.toLowerCase().includes(trimmed.toLowerCase())),
  )
  const exactMatchExists =
    trimmed.length > 0 && (availableTags.includes(trimmed) || value.includes(trimmed))

  const addTag = (raw: string) => {
    const tag = raw.trim()
    if (tag.length === 0 || tag.length > maxLength) return
    if (value.includes(tag)) return
    onChange([...value, tag])
    setQuery('')
  }

  const removeTag = (tag: string) => {
    onChange(value.filter((t) => t !== tag))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && trimmed.length > 0) {
      e.preventDefault()
      addTag(trimmed)
    } else if (e.key === 'Backspace' && query.length === 0 && value.length > 0) {
      removeTag(value[value.length - 1])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <div
        className={cn(
          'flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm cursor-text',
          open && 'ring-1 ring-ring',
          disabled && 'pointer-events-none opacity-50',
        )}
        onClick={() => {
          if (disabled) return
          inputRef.current?.focus()
          setOpen(true)
        }}
      >
        {value.map((tag) => (
          <Badge key={tag} variant="secondary" className="gap-1">
            <span>{tag}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                removeTag(tag)
              }}
              className="ml-0.5 rounded-sm outline-none ring-offset-background hover:opacity-70 focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label={`移除 ${tag}`}
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            const next = e.target.value
            if (next.length <= maxLength) {
              setQuery(next)
              setOpen(true)
            }
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
          className="flex-1 min-w-[120px] bg-transparent outline-none placeholder:text-muted-foreground"
        />
      </div>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md max-h-64 overflow-y-auto">
          {suggestions.length > 0 && (
            <div className="py-1">
              <div className="px-2 py-1 text-xs text-muted-foreground">建議</div>
              {suggestions.slice(0, 50).map((tag) => (
                <button
                  type="button"
                  key={tag}
                  onClick={() => addTag(tag)}
                  className="block w-full px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground rounded-sm"
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          {trimmed.length > 0 && !exactMatchExists && (
            <div className={cn('py-1', suggestions.length > 0 && 'border-t')}>
              <div className="px-2 py-1 text-xs text-muted-foreground">新增</div>
              <button
                type="button"
                onClick={() => addTag(trimmed)}
                className="block w-full px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground rounded-sm"
              >
                新增「{trimmed}」
              </button>
            </div>
          )}

          {suggestions.length === 0 && (trimmed.length === 0 || exactMatchExists) && (
            <div className="px-2 py-3 text-center text-sm text-muted-foreground">
              輸入文字以新增或搜尋標籤
            </div>
          )}
        </div>
      )}
    </div>
  )
}
